import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import { Button } from '@mui/material';

import type { DashboardVariableType } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { useSection } from 'src/components/app/context/SectionContext';
import type { AccordionProps } from 'src/components/common/Accordion';
import Accordion, {
  AccordionItem,
  AccordionItemHeader,
  AccordionItemPanel,
} from 'src/components/common/Accordion';
import CustomItemList from 'src/components/common/CustomItemList';
import OrgTree from 'src/components/common/OrgTree';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import WorkspaceVariableList from 'src/components/workspace/components/WorkspaceVariableList';

export const WORKSPACE_ACCORDION_TEST_ID = 'workspace-accordion';

export const WorkspaceAccordion: React.FC<AccordionProps> = (props) => {
  const { i18n, t } = useTranslation('report');
  const [variableTypes, setVariableTypes] = useState<DashboardVariableType[]>(
    [],
  );
  const { moduleId } = useSection();
  const orgTreeState = useOrgTreeState();

  /**
   * @description get variable types for dataline filters.
   */
  const getVariableTypes = () => {
    if (!moduleId) return;

    HttpService.get(apiPaths.VARIABLE_TYPE, { module_id: moduleId })
      .then(handleResponse)
      .catch(handleError);
  };

  /**
   * @description Handles the variableType endpoint response.
   * @param response
   */
  const handleResponse = (response: {
    data: { results: DashboardVariableType[] };
  }) => {
    const {
      data: { results: variableTypes },
    } = response;

    setVariableTypes(variableTypes);
  };

  /**
   * @description Handles the variableType endpoint errors.
   * @param error
   */
  const handleError = (error: { message: string }) => {
    const message = error ? `"${error.message}"` : t('common:unknownError');

    setVariableTypes([
      { name: t('sidebar.variableError', { message }), id: 0, variables: [] },
    ]);
  };

  // Reload VariableTypes on language change.
  useEffect(() => {
    getVariableTypes();
  }, [i18n.language, moduleId]);

  // Make sure the moduleId is set before rendering.
  if (!moduleId) return null;

  return (
    <Accordion
      data-test-id={WORKSPACE_ACCORDION_TEST_ID}
      initiallyActiveItems={['org-tree-accordion-item']}
      {...props}
    >
      <AccordionItem
        itemName="org-tree-accordion-item"
        aria-label={t('sidebar.title')}
      >
        <AccordionItemHeader headline={t('sidebar.title')} />

        <AccordionItemPanel>
          <OrgTree orgTreeState={orgTreeState} itemsAreDraggable={true} />
        </AccordionItemPanel>
      </AccordionItem>
      {!!variableTypes.length &&
        variableTypes.map((variableType: DashboardVariableType) => (
          <AccordionItem
            key={variableType.name}
            itemName={`${variableType.name
              .replace(/\s+/g, '-')
              .toLowerCase()}-accordion-item`}
          >
            <AccordionItemHeader headline={variableType.name} />

            <AccordionItemPanel>
              <WorkspaceVariableList
                moduleId={moduleId}
                variableTypeId={variableType.id}
                searchInputPlaceholderFirst={t(
                  'report:searchPlaceholderSimple',
                )}
                searchInputPlaceholderValue={t('report:searchPlaceholderValue')}
              />
            </AccordionItemPanel>
          </AccordionItem>
        ))}
      <AccordionItem
        itemName="my-datalines--accordion-item"
        aria-label={t('sidebar.myDataLines')}
      >
        <AccordionItemHeader headline={t('sidebar.myDataLines')} />

        <AccordionItemPanel>
          <CustomItemList customItemType="datalines" infiniteScrolling />
        </AccordionItemPanel>
      </AccordionItem>
      <AccordionItem
        itemName="custom-reports--accordion-item"
        aria-label={t('sidebar.myReports')}
      >
        <AccordionItemHeader headline={t('sidebar.myReports')}>
          <Button
            variant="squareCta"
            onClick={() => history.push(routePaths.MY_REPORTS)}
            sx={{
              p: 0.5,
              minHeight: 0,
            }}
          >
            <ShoppingBasketRoundedIcon sx={{ fontSize: '1rem' }} />
          </Button>
        </AccordionItemHeader>

        <AccordionItemPanel>
          <CustomItemList customItemType="reports" infiniteScrolling />
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default WorkspaceAccordion;
